import React, { useState, useRef, useEffect } from 'react';
import { API_BASE_URL } from '../config';

const SLUGS = [
  { slug: 'falstaff', label: 'Falstaff' },
  { slug: 'lady_macbeth', label: 'Lady Macbeth' },
  { slug: 'iago', label: 'Iago' },
  { slug: 'miranda', label: 'Miranda' },
  { slug: 'sir_andrew', label: 'Sir Andrew' },
];

// Helper to get character from URL hash (e.g., #characters?char=lady_macbeth)
function getCharFromHash() {
  const hash = window.location.hash;
  if (hash.includes('?')) {
    const queryPart = hash.split('?')[1];
    const params = new URLSearchParams(queryPart);
    const charParam = params.get('char');
    if (charParam) {
      const found = SLUGS.find(s => s.slug === charParam);
      if (found) {
        console.log('[CharacterChat] Deep-link: starting with', found.label);
        return found;
      }
    }
  }
  return SLUGS[0]; // Default to Falstaff
}

export default function CharacterChat() {
  const initialChar = getCharFromHash();
  const [slug, setSlug] = useState(initialChar.slug);
  const [label, setLabel] = useState(initialChar.label);
  const [avatars, setAvatars] = useState({});
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const endRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/ui/chat/avatars`);
        const dj = await r.json();
        if (alive) setAvatars(dj || {});
      } catch {}
    })();
    return () => { alive = false };
  }, []);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const onSelect = (e) => {
    const val = e.target.value;
    setSlug(val);
    const found = SLUGS.find(s => s.slug === val);
    setLabel(found ? found.label : val);
    setMessages([]);
  };

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setMessages(prev => [...prev, { who: 'you', text }]);
    setInput('');
    try {
      const r = await fetch(`${API_BASE_URL}/api/ui/chat/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text })
      });
      const dj = await r.json();
      const reply = (dj && dj.reply) || '(no reply)';
      setMessages(prev => [...prev, { who: 'them', text: reply }]);
      // Track character chat engagement
      if (window.gtag) {
        window.gtag('event', 'character_chat', {
          character: slug,
          message_count: messages.length + 1
        });
      }
    } catch (e) {
      setMessages(prev => [...prev, { who: 'them', text: '(failed to send)' }]);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const avatarFor = (lbl) => {
    const key = (lbl || '').toLowerCase();
    return avatars[key] || avatars[slug] || avatars[slug.replace(/_/g, ' ')] || '';
  };

  return (
    <div className="console-container">
      <div className="console-left" style={{ width: '100%' }}>
        <div className="console-panel chat-console">
          <div className="chat-header">
            <select value={slug} onChange={onSelect} className="chat-select">
              {SLUGS.map(s => (
                <option key={s.slug} value={s.slug}>{s.label}</option>
              ))}
            </select>
            <div className="chat-title">
              <span className="avatar lg" style={{ backgroundImage: `url(${avatarFor(label)})` }} />
              <span className="chat-name">{label}</span>
            </div>
          </div>
          <p className="chat-stage-note">
            Talk here for a live reply. On Bluesky the troupe posts and replies in public;
            @mentions aren&apos;t a chat inbox.
          </p>
          <div className="chat-body">
            {messages.length === 0 && (
              <div className="chat-empty-hint">Say something to {label}.</div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`chat-line ${m.who==='you'?'user':'char'}`}>
                {m.who==='you' ? (<>
                  <strong className="user-name">&lt;user&gt;:</strong> <span className="user-text">{m.text}</span>
                </>) : (
                  <>
                    <span className="avatar sm" style={{ backgroundImage: `url(${avatarFor(label)})` }} />
                    <strong className="char-name">{label}:</strong> <span className="char-text">{m.text}</span>
                  </>
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="chat-input">
            <textarea rows={3} value={input} onChange={e=>setInput(e.target.value)} onKeyDown={onKey} placeholder={`Address ${label}…`} />
            <button onClick={send}>Send</button>
          </div>
        </div>
      </div>
    </div>
  );
}
